// client/src/components/pages/LexiStar/Approvals/approvalParts.tsx
//
// The pieces the approvals page's two layouts share (ST-00207 phone pass; moved out of
// Approvals.tsx unchanged): the deviation row shape, the typed views of the JS components the
// page uses, the decision form (reason code, extra km, temporary corridor), the driver's reply
// and the trip context. The desktop split view (Approvals.tsx) and the phone queue / detail
// (ApprovalsPhone.tsx) both render them.
//
// Phone variant of the decision form (`phone`): the approve form carries the Approve once /
// Approve + temporary corridor choice (the phone's action bar has one Approve button), 44 px
// fields that do not open the keyboard for the reason, large radio rows for the corridor scope,
// and full-width Cancel / Confirm buttons.

import { useState, type ComponentType, type ReactNode } from 'react';
import type { IconType } from 'react-icons';
import {
  PageHeader as PageHeaderJs,
  EmptyState as EmptyStateJs,
  SeverityChip as SeverityChipJs,
  StagePill,
  ReasonCodeSelect,
  DateTimeField as DateTimeFieldJs,
  inputClasses,
} from '../StatusBoard/atoms';
import { fmtNum, fmtKm, fmtL, fmtTime } from '../StatusBoard/format';
import CorridorMapJs from './CorridorMap';
import { ACTIONS } from './approvalLogic';
import DriverPhotos from './DriverPhotos';
import { driverReplyOf } from './driverReply';
import { phoneActionClass } from '../../../common/PhoneActionBar';
import type { DriverMediaLike } from '../../../../types/lexistar-media';

export const LIVE_REFETCH_DEBOUNCE_MS = 1000;
export const DEFAULT_CORRIDOR_HOURS = 12;

// ── Shapes (m2-contracts §4.2 DeviationRow; the server may send more) ────────

export type Num = number | string | null | undefined;

export interface DeviationTrip {
  id?: string;
  tenant_id?: number | null;
  reference?: string | null;
  stage?: string | null;
  registration?: string | null;
  driver_name?: string | null;
  driver_tag?: string | null;
  route?: { id: string; code?: string | null; name?: string | null; version?: number | null } | null;
  from_name?: string | null;
  to_name?: string | null;
  load?: { material?: string | null; order_ref?: string | null } | null;
  planned_km?: Num;
  adjusted_km?: Num;
  budget_adjusted_l?: Num;
}

export interface DeviationRow {
  id: string;
  trip_id: string;
  asset_key?: string | null;
  state: string;
  severity?: string | null;
  opened_at: string;
  sla_due_at?: string | null;
  max_offset_m?: Num;
  off_route_km?: Num;
  confidence?: number | null;
  stops?: unknown[] | null;
  track?: unknown;
  position?: { ts: string; lat: number; lon: number; speed_kmh?: number | null } | null;
  can_decide?: boolean;
  decided_by?: string | null;
  decided_at?: string | null;
  reason_code?: string | null;
  /** As the engine stored it ({reason_code, text, media, at, …}) or the UI's reading ({code, text, photo_url, replied_at}). */
  driver_reason?: unknown;
  /** The driver's photos ({kind, ref, mime_type, bytes, sha256}); an s3:// ref is viewable. */
  driver_reason_media?: unknown;
  trip?: DeviationTrip | null;
}

export interface ReasonCode {
  code: string;
  label: string;
}

export type ActionId = 'approve' | 'approve_corridor' | 'reject' | 'escalate';

export interface DecisionPayload {
  reason_code: string;
  extra_km?: number;
  create_corridor?: { scope: 'trip' | 'route'; expiry: string };
}

export type DecideFn = (action: ActionId, payload: DecisionPayload, reasonLabel: string) => void;

/** States in which a deviation still waits for a decision (the Decision section offers actions). */
export const UNDECIDED_STATES = ['OPEN', 'AWAITING_DECISION', 'ESCALATED', 'SUSPECTED'];

/** States in which the keyboard shortcuts / action bar may open a decision. */
export const DECIDABLE_STATES = ['OPEN', 'AWAITING_DECISION', 'ESCALATED'];

export const registrationOf = (row: DeviationRow): string =>
  row.trip?.registration || row.asset_key || '';

// ── Typed views of the JS pieces this page uses ─────────────────────────────

export const PageHeader = PageHeaderJs as unknown as ComponentType<{
  icon?: IconType;
  title: string;
  subtitle?: ReactNode;
  toolbar?: ReactNode;
  actions?: ReactNode;
}>;

export const EmptyState = EmptyStateJs as unknown as ComponentType<{
  icon?: IconType;
  title: string;
  children?: ReactNode;
}>;

export const SeverityChip = SeverityChipJs as unknown as ComponentType<{ severity?: string | null }>;

export const DateTimeField = DateTimeFieldJs as unknown as ComponentType<{
  id: string;
  value: Date | null;
  onChange: (d: Date | null) => void;
  minDate?: Date;
}>;

export const CorridorMap = CorridorMapJs as unknown as ComponentType<{
  idPrefix?: string;
  routes?: unknown[];
  corridors?: unknown[];
  track?: unknown;
  deviations?: Array<{ id: string; geometry: unknown; approved: boolean }>;
  position?: DeviationRow['position'];
  positionColor?: string;
  stops?: unknown[];
  className?: string;
}>;

export const errMessage = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { error?: string } }; message?: string } | null;
  return e?.response?.data?.error || e?.message || fallback;
};

// ── Decision form ───────────────────────────────────────────────────────────

export function DecisionPanel({
  action,
  row,
  codes,
  onCancel,
  onConfirm,
  busy,
  error,
  phone = false,
  onApproveMode,
}: {
  action: ActionId;
  row: DeviationRow;
  codes: ReasonCode[];
  onCancel: () => void;
  onConfirm: DecideFn;
  busy: boolean;
  error: string | null;
  /** ST-00207: the phone layout of the form. */
  phone?: boolean;
  /** Phone: switch between Approve once and Approve + temporary corridor (the form keeps its values). */
  onApproveMode?: (action: 'approve' | 'approve_corridor') => void;
}) {
  const [reasonCode, setReasonCode] = useState('');
  const [extraKm, setExtraKm] = useState('');
  const [scope, setScope] = useState<'trip' | 'route'>('trip');
  const [expiry, setExpiry] = useState<Date | null>(
    () => new Date(Date.now() + DEFAULT_CORRIDOR_HOURS * 3600 * 1000)
  );
  const [localError, setLocalError] = useState<string | null>(null);
  const meta = ACTIONS[action];
  const isApprove = action === 'approve' || action === 'approve_corridor';

  const submit = () => {
    if (!reasonCode) {
      setLocalError('Choose a reason — every decision is audited with one');
      return;
    }
    if (action === 'approve_corridor' && !expiry) {
      setLocalError('Set when the temporary corridor expires');
      return;
    }
    const km = extraKm === '' ? undefined : Number(extraKm);
    if (km !== undefined && (!Number.isFinite(km) || km < 0)) {
      setLocalError('Extra km must be zero or more');
      return;
    }
    setLocalError(null);
    const payload: DecisionPayload = { reason_code: reasonCode };
    if (isApprove && km !== undefined) payload.extra_km = km;
    if (action === 'approve_corridor' && expiry) {
      payload.create_corridor = { scope, expiry: expiry.toISOString() };
    }
    onConfirm(action, payload, codes.find((c) => c.code === reasonCode)?.label || reasonCode);
  };

  if (phone) {
    return (
      <div
        role="group"
        aria-label={`${meta.label} — confirm`}
        className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-4 scroll-mb-28"
        data-decision-form=""
      >
        <p className="text-base font-semibold text-slate-900">{meta.label}</p>
        {(localError || error) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
            <p className="text-sm text-red-600">{localError || error}</p>
          </div>
        )}
        {isApprove && onApproveMode && (
          <fieldset className="phone-choices">
            <legend className="phone-choices__legend">Approve</legend>
            <div className="phone-choices__list">
              <label className="phone-choice">
                <input
                  type="radio"
                  name={`approve-mode-${row.id}`}
                  checked={action === 'approve'}
                  onChange={() => onApproveMode('approve')}
                />
                <span className="phone-choice__text">
                  <span>
                    Once
                    <span className="phone-choice__hint">This detour only</span>
                  </span>
                </span>
              </label>
              <label className="phone-choice">
                <input
                  type="radio"
                  name={`approve-mode-${row.id}`}
                  checked={action === 'approve_corridor'}
                  onChange={() => onApproveMode('approve_corridor')}
                />
                <span className="phone-choice__text">
                  <span>
                    With a temporary corridor
                    <span className="phone-choice__hint">The detour stays allowed until it expires</span>
                  </span>
                </span>
              </label>
            </div>
          </fieldset>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
          <ReasonCodeSelect
            codes={codes}
            value={reasonCode}
            onChange={setReasonCode}
            className={`${inputClasses} h-11`}
            isSearchable={false}
          />
        </div>
        {action === 'approve_corridor' && (
          <>
            <fieldset className="phone-choices">
              <legend className="phone-choices__legend">Applies to</legend>
              <div className="phone-choices__list">
                <label className="phone-choice">
                  <input
                    type="radio"
                    name="corridor-scope"
                    value="trip"
                    checked={scope === 'trip'}
                    onChange={() => setScope('trip')}
                  />
                  <span className="phone-choice__text">This trip ({row.trip?.reference})</span>
                </label>
                <label className="phone-choice">
                  <input
                    type="radio"
                    name="corridor-scope"
                    value="route"
                    checked={scope === 'route'}
                    onChange={() => setScope('route')}
                  />
                  <span className="phone-choice__text">
                    All trips on {row.trip?.route?.code || 'the route'} (advisory)
                  </span>
                </label>
              </div>
            </fieldset>
            <div>
              <label htmlFor="lexistar-corridor-expiry" className="block text-sm font-medium text-slate-700 mb-1">
                Expires
              </label>
              <DateTimeField id="lexistar-corridor-expiry" value={expiry} onChange={setExpiry} minDate={new Date()} />
            </div>
          </>
        )}
        {isApprove && (
          <div>
            <label htmlFor="lexistar-extra-km" className="block text-sm font-medium text-slate-700 mb-1">
              Extra km to allow <span className="font-normal text-slate-500">(optional)</span>
            </label>
            <input
              id="lexistar-extra-km"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              value={extraKm}
              onChange={(e) => setExtraKm(e.target.value)}
              placeholder={`Empty = ${fmtNum(row.off_route_km, 1)} km so far`}
              className={`${inputClasses} h-11 text-base`}
            />
            <p className="mt-1 text-xs text-slate-500">
              Approval re-baselines the planned distance and the litre allowance; originals are kept.
            </p>
          </div>
        )}
        <div className="phone-actions">
          <button type="button" onClick={onCancel} className={phoneActionClass()}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            aria-label={busy ? undefined : `Confirm: ${meta.label}`}
            className={phoneActionClass('primary')}
          >
            {busy ? 'Saving...' : 'Confirm'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={`${meta.label} — confirm`}
      className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-3"
    >
      <p className="text-sm font-semibold text-slate-900">{meta.label}</p>
      {(localError || error) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{localError || error}</p>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
        <ReasonCodeSelect codes={codes} value={reasonCode} onChange={setReasonCode} autoFocus />
      </div>
      {action === 'approve_corridor' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <fieldset>
            <legend className="block text-sm font-medium text-slate-700 mb-1">Applies to</legend>
            <div className="flex flex-col gap-1 text-sm text-slate-700">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="corridor-scope"
                  value="trip"
                  checked={scope === 'trip'}
                  onChange={() => setScope('trip')}
                />
                This trip ({row.trip?.reference})
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="corridor-scope"
                  value="route"
                  checked={scope === 'route'}
                  onChange={() => setScope('route')}
                />
                All trips on {row.trip?.route?.code || 'the route'} (advisory)
              </label>
            </div>
          </fieldset>
          <div>
            <label
              htmlFor="lexistar-corridor-expiry"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Expires
            </label>
            <DateTimeField
              id="lexistar-corridor-expiry"
              value={expiry}
              onChange={setExpiry}
              minDate={new Date()}
            />
          </div>
        </div>
      )}
      {isApprove && (
        <div>
          <label
            htmlFor="lexistar-extra-km"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Extra km to allow <span className="font-normal text-slate-500">(optional)</span>
          </label>
          <input
            id="lexistar-extra-km"
            type="number"
            min="0"
            step="0.1"
            value={extraKm}
            onChange={(e) => setExtraKm(e.target.value)}
            placeholder={`Empty = actual off-route distance (${fmtNum(row.off_route_km, 1)} km so far)`}
            className={inputClasses}
          />
          <p className="mt-1 text-xs text-slate-500">
            Approval re-baselines the planned distance and the litre allowance; originals are kept.
          </p>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className={`px-5 py-2 text-sm font-semibold rounded-lg shadow-sm ${busy ? 'bg-slate-400 text-white cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600 text-white'}`}
        >
          {busy ? 'Saving...' : `Confirm: ${meta.label}`}
        </button>
      </div>
    </div>
  );
}

// ── Driver's reply / trip context (the contents of their sections) ──────────

/** What the driver answered: the reason, their words, their photos, when they replied. */
export function DriverReplyContent({
  row,
  codes,
  photos,
}: {
  row: DeviationRow;
  codes: ReasonCode[];
  photos: readonly DriverMediaLike[];
}) {
  const reply = driverReplyOf(row.driver_reason);
  const reasonLabel = (code: string | null | undefined) =>
    codes.find((c) => c.code === code)?.label || code;
  return reply ? (
    <div className="text-sm text-slate-700 space-y-1">
      <p className="font-semibold text-slate-900">{reasonLabel(reply.code)}</p>
      {reply.text && <p>"{reply.text}"</p>}
      {photos.length > 0 && (
        <DriverPhotos media={photos} subject={row.trip?.registration || row.asset_key || undefined} />
      )}
      {photos.length === 0 && reply.photoUrl && (
        <a
          href={reply.photoUrl}
          target="_blank"
          rel="noreferrer"
          className="text-primary-700 hover:underline"
        >
          View photo
        </a>
      )}
      {reply.repliedAt && <p className="text-xs text-slate-500">Replied {fmtTime(reply.repliedAt)}</p>}
    </div>
  ) : (
    <p className="text-sm text-slate-500">
      No reply yet. The driver has been asked for a reason; you can decide without one.
    </p>
  );
}

/** Stage, load, planned distance and allowance of the deviation's trip. */
export function TripContextList({ row }: { row: DeviationRow }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      <dt className="text-slate-500">Stage</dt>
      <dd>
        <StagePill stage={row.trip?.stage} />
      </dd>
      <dt className="text-slate-500">Load</dt>
      <dd className="text-slate-900">
        {[row.trip?.load?.material, row.trip?.load?.order_ref].filter(Boolean).join(' · ') || '—'}
      </dd>
      <dt className="text-slate-500">Planned</dt>
      <dd className="text-slate-900 tabular-nums">
        {fmtKm(row.trip?.planned_km)}
        {row.trip?.adjusted_km != null && Number(row.trip.adjusted_km) !== Number(row.trip.planned_km) && (
          <> → {fmtKm(row.trip.adjusted_km)} adjusted</>
        )}
      </dd>
      <dt className="text-slate-500">Allowance</dt>
      <dd className="text-slate-900 tabular-nums">{fmtL(row.trip?.budget_adjusted_l)}</dd>
    </dl>
  );
}

/**
 * What the Decision section says instead of offering actions: "Approved by … at … — reason.",
 * the permission note, or the other organisation's note. Null while the user may decide.
 */
export function decisionStatusNote({
  row,
  canDecide,
  codes,
}: {
  row: DeviationRow;
  canDecide: boolean;
  codes: ReasonCode[];
}): ReactNode {
  const reasonLabel = (code: string | null | undefined) =>
    codes.find((c) => c.code === code)?.label || code;
  if (!UNDECIDED_STATES.includes(row.state)) {
    return (
      <p className="text-sm text-slate-700">
        {row.state === 'APPROVED' ? 'Approved' : row.state === 'REJECTED' ? 'Rejected' : 'Closed'}
        {row.decided_by && ` by ${row.decided_by}`}
        {row.decided_at && ` at ${fmtTime(row.decided_at)}`}
        {row.reason_code && ` — ${reasonLabel(row.reason_code)}`}.
      </p>
    );
  }
  if (!canDecide) {
    return (
      <p className="text-sm text-slate-500">
        Deciding on deviations needs the{' '}
        <span className="font-mono">lexistar.deviations.approve</span> permission.
      </p>
    );
  }
  if (row.can_decide === false) {
    return (
      <p className="text-sm text-slate-500">
        This trip belongs to another organisation; its transporter decides on the deviation.
      </p>
    );
  }
  return null;
}
